import { defineDspModule, type DspModuleInstance, type DspProcessBlock } from '../../dsp-module-contract/src/index.js';

export const MAX_VOWEL_BANKS = 8;
export const FORMANTS_PER_BANK = 6;

export interface FormantDefinition {
  readonly frequencyHz: number;
  readonly bandwidthHz: number;
  readonly gainDb: number;
}

export interface FormantBankDefinition {
  readonly name: string;
  readonly formants: readonly FormantDefinition[];
}

export interface FormantVowelFilterBankOptions {
  readonly sampleRate: number;
  readonly banks?: readonly FormantBankDefinition[];
  readonly bankA?: number;
  readonly bankB?: number;
  readonly morph?: number;
  readonly bandwidth?: number;
  readonly outputGain?: number;
}

const rawDefaultBanks: readonly FormantBankDefinition[] = [
  { name: 'a', formants: [
    { frequencyHz: 800, bandwidthHz: 80, gainDb: 0 }, { frequencyHz: 1_150, bandwidthHz: 90, gainDb: -4 },
    { frequencyHz: 2_900, bandwidthHz: 120, gainDb: -9 }, { frequencyHz: 3_900, bandwidthHz: 180, gainDb: -12 },
    { frequencyHz: 4_950, bandwidthHz: 250, gainDb: -18 }, { frequencyHz: 5_800, bandwidthHz: 300, gainDb: -24 },
  ] },
  { name: 'e', formants: [
    { frequencyHz: 500, bandwidthHz: 70, gainDb: 0 }, { frequencyHz: 1_750, bandwidthHz: 100, gainDb: -5 },
    { frequencyHz: 2_450, bandwidthHz: 120, gainDb: -9 }, { frequencyHz: 3_350, bandwidthHz: 180, gainDb: -14 },
    { frequencyHz: 4_500, bandwidthHz: 240, gainDb: -20 }, { frequencyHz: 5_500, bandwidthHz: 300, gainDb: -25 },
  ] },
  { name: 'i', formants: [
    { frequencyHz: 300, bandwidthHz: 60, gainDb: 0 }, { frequencyHz: 2_200, bandwidthHz: 100, gainDb: -4 },
    { frequencyHz: 3_000, bandwidthHz: 120, gainDb: -10 }, { frequencyHz: 3_700, bandwidthHz: 180, gainDb: -15 },
    { frequencyHz: 4_700, bandwidthHz: 240, gainDb: -21 }, { frequencyHz: 5_700, bandwidthHz: 300, gainDb: -26 },
  ] },
  { name: 'o', formants: [
    { frequencyHz: 500, bandwidthHz: 70, gainDb: 0 }, { frequencyHz: 900, bandwidthHz: 80, gainDb: -5 },
    { frequencyHz: 2_600, bandwidthHz: 120, gainDb: -12 }, { frequencyHz: 3_300, bandwidthHz: 180, gainDb: -17 },
    { frequencyHz: 4_300, bandwidthHz: 240, gainDb: -22 }, { frequencyHz: 5_300, bandwidthHz: 300, gainDb: -27 },
  ] },
  { name: 'u', formants: [
    { frequencyHz: 350, bandwidthHz: 60, gainDb: 0 }, { frequencyHz: 700, bandwidthHz: 80, gainDb: -5 },
    { frequencyHz: 2_400, bandwidthHz: 120, gainDb: -14 }, { frequencyHz: 3_200, bandwidthHz: 180, gainDb: -18 },
    { frequencyHz: 4_200, bandwidthHz: 240, gainDb: -23 }, { frequencyHz: 5_200, bandwidthHz: 300, gainDb: -28 },
  ] },
  { name: 'y', formants: [
    { frequencyHz: 300, bandwidthHz: 60, gainDb: 0 }, { frequencyHz: 1_700, bandwidthHz: 90, gainDb: -5 },
    { frequencyHz: 2_300, bandwidthHz: 110, gainDb: -11 }, { frequencyHz: 3_200, bandwidthHz: 170, gainDb: -16 },
    { frequencyHz: 4_300, bandwidthHz: 230, gainDb: -22 }, { frequencyHz: 5_300, bandwidthHz: 290, gainDb: -27 },
  ] },
  { name: 'ae', formants: [
    { frequencyHz: 660, bandwidthHz: 75, gainDb: 0 }, { frequencyHz: 1_700, bandwidthHz: 95, gainDb: -4 },
    { frequencyHz: 2_400, bandwidthHz: 120, gainDb: -10 }, { frequencyHz: 3_300, bandwidthHz: 175, gainDb: -15 },
    { frequencyHz: 4_400, bandwidthHz: 235, gainDb: -21 }, { frequencyHz: 5_400, bandwidthHz: 295, gainDb: -26 },
  ] },
  { name: 'oe', formants: [
    { frequencyHz: 450, bandwidthHz: 65, gainDb: 0 }, { frequencyHz: 1_100, bandwidthHz: 85, gainDb: -5 },
    { frequencyHz: 2_200, bandwidthHz: 115, gainDb: -12 }, { frequencyHz: 3_100, bandwidthHz: 170, gainDb: -17 },
    { frequencyHz: 4_100, bandwidthHz: 230, gainDb: -22 }, { frequencyHz: 5_100, bandwidthHz: 290, gainDb: -27 },
  ] },
];

const freezeBanks = (banks: readonly FormantBankDefinition[]): readonly FormantBankDefinition[] => Object.freeze(
  banks.map((bank) => Object.freeze({
    name: bank.name,
    formants: Object.freeze(bank.formants.map((formant) => Object.freeze({ ...formant }))),
  })),
);

export const DEFAULT_VOWEL_BANKS = freezeBanks(rawDefaultBanks);

const DENORMAL = 1e-30;
const assertRange = (label: string, value: number, minimum: number, maximum: number): void => {
  if (!Number.isFinite(value) || value < minimum || value > maximum) {
    throw new RangeError(`${label} must be finite and in [${minimum}, ${maximum}]`);
  }
};
const flush = (value: number): number => Math.abs(value) < DENORMAL ? 0 : value;

const validateBanks = (banks: readonly FormantBankDefinition[]): readonly FormantBankDefinition[] => {
  if (banks.length < 1 || banks.length > MAX_VOWEL_BANKS) throw new RangeError(`banks must contain between 1 and ${MAX_VOWEL_BANKS} banks`);
  for (const [bankIndex, bank] of banks.entries()) {
    if (!bank.name.trim()) throw new RangeError(`bank ${bankIndex} name is required`);
    if (bank.formants.length !== FORMANTS_PER_BANK) throw new RangeError(`bank ${bankIndex} must contain exactly ${FORMANTS_PER_BANK} formants`);
    for (const [formantIndex, formant] of bank.formants.entries()) {
      assertRange(`bank ${bankIndex} formant ${formantIndex} frequencyHz`, formant.frequencyHz, 40, 20_000);
      assertRange(`bank ${bankIndex} formant ${formantIndex} bandwidthHz`, formant.bandwidthHz, 10, 10_000);
      assertRange(`bank ${bankIndex} formant ${formantIndex} gainDb`, formant.gainDb, -48, 12);
    }
  }
  return freezeBanks(banks);
};

export class FormantVowelFilterBank {
  readonly sampleRate: number;
  readonly bankCount: number;
  private readonly banks: readonly FormantBankDefinition[];
  private bankA: number;
  private bankB: number;
  private morph: number;
  private bandwidth: number;
  private outputGain: number;
  private coefficientsDirty = true;
  private coefficientMorph = Number.NaN;
  private readonly b0 = new Float64Array(FORMANTS_PER_BANK);
  private readonly b2 = new Float64Array(FORMANTS_PER_BANK);
  private readonly a1 = new Float64Array(FORMANTS_PER_BANK);
  private readonly a2 = new Float64Array(FORMANTS_PER_BANK);
  private readonly gain = new Float64Array(FORMANTS_PER_BANK);
  private readonly z1 = new Float64Array(FORMANTS_PER_BANK);
  private readonly z2 = new Float64Array(FORMANTS_PER_BANK);

  constructor(options: FormantVowelFilterBankOptions) {
    assertRange('sampleRate', options.sampleRate, 8_000, 384_000);
    this.sampleRate = options.sampleRate;
    this.banks = options.banks === undefined ? DEFAULT_VOWEL_BANKS : validateBanks(options.banks);
    this.bankCount = this.banks.length;
    this.bankA = options.bankA ?? 0;
    this.bankB = options.bankB ?? Math.min(1, this.bankCount - 1);
    this.morph = options.morph ?? 0;
    this.bandwidth = options.bandwidth ?? 1;
    this.outputGain = options.outputGain ?? 1;
    this.assertBank('bankA', this.bankA);
    this.assertBank('bankB', this.bankB);
    assertRange('morph', this.morph, 0, 1);
    assertRange('bandwidth', this.bandwidth, 0.25, 4);
    assertRange('outputGain', this.outputGain, 0, 2);
  }

  reset(): void {
    this.z1.fill(0);
    this.z2.fill(0);
    this.coefficientsDirty = true;
    this.coefficientMorph = Number.NaN;
  }

  setParameter(id: string, value: number): void {
    if (id === 'bank-a' || id === 'bank-b') {
      this.assertBank(id, value);
      if (id === 'bank-a') this.bankA = value; else this.bankB = value;
      this.coefficientsDirty = true;
      return;
    }
    if (id === 'morph') { assertRange(id, value, 0, 1); this.morph = value; return; }
    if (id === 'bandwidth') { assertRange(id, value, 0.25, 4); this.bandwidth = value; this.coefficientsDirty = true; return; }
    if (id === 'output-gain') { assertRange(id, value, 0, 2); this.outputGain = value; return; }
    throw new RangeError(`unknown parameter: ${id}`);
  }

  processInto(input: Float32Array, output: Float32Array, morph?: Float32Array, frames = output.length): void {
    if (!Number.isInteger(frames) || frames < 0 || input.length < frames || output.length < frames || (morph && morph.length < frames)) {
      throw new RangeError('buffers are too short for frames');
    }
    // Validate the complete block before touching caller output or recursive
    // filter state. Hosts can then discard a rejected block without a partial
    // render or a hidden state advance affecting the next valid block.
    for (let frame = 0; frame < frames; frame += 1) {
      if (!Number.isFinite(input[frame]!)) throw new RangeError('input samples must be finite');
      const position = morph ? morph[frame]! : this.morph;
      if (!Number.isFinite(position) || position < 0 || position > 1) throw new RangeError('morph samples must be finite and in [0, 1]');
    }
    for (let frame = 0; frame < frames; frame += 1) {
      const sample = input[frame]!;
      const position = morph ? morph[frame]! : this.morph;
      if (this.coefficientsDirty || position !== this.coefficientMorph) this.updateCoefficients(position);
      let sum = 0;
      for (let formant = 0; formant < FORMANTS_PER_BANK; formant += 1) {
        const value = this.b0[formant]! * sample + this.z1[formant]!;
        this.z1[formant] = flush(-this.a1[formant]! * value + this.z2[formant]!);
        this.z2[formant] = flush(this.b2[formant]! * sample - this.a2[formant]! * value);
        if (!Number.isFinite(value) || !Number.isFinite(this.z1[formant]!) || !Number.isFinite(this.z2[formant]!)) {
          this.z1[formant] = 0; this.z2[formant] = 0;
        } else sum += value * this.gain[formant]!;
      }
      const scaled = sum * this.outputGain;
      output[frame] = Number.isFinite(scaled) ? Math.tanh(scaled) : 0;
    }
  }

  private assertBank(label: string, value: number): void {
    if (!Number.isInteger(value) || value < 0 || value >= this.bankCount) throw new RangeError(`${label} bank index must be an integer in [0, ${this.bankCount - 1}]`);
  }

  /** Constant-peak-gain RBJ band-pass; bounded Q and sub-Nyquist clamping keep poles stable. */
  private updateCoefficients(morph: number): void {
    const left = this.banks[this.bankA]!;
    const right = this.banks[this.bankB]!;
    const maximumFrequency = this.sampleRate * 0.45;
    for (let index = 0; index < FORMANTS_PER_BANK; index += 1) {
      const a = left.formants[index]!;
      const b = right.formants[index]!;
      const frequency = Math.min(maximumFrequency, Math.exp(Math.log(a.frequencyHz) + (Math.log(b.frequencyHz) - Math.log(a.frequencyHz)) * morph));
      const bandwidth = (a.bandwidthHz + (b.bandwidthHz - a.bandwidthHz) * morph) * this.bandwidth;
      const q = Math.max(0.25, Math.min(50, frequency / bandwidth));
      const omega = 2 * Math.PI * frequency / this.sampleRate;
      const alpha = Math.sin(omega) / (2 * q);
      const reciprocalA0 = 1 / (1 + alpha);
      this.b0[index] = alpha * reciprocalA0;
      this.b2[index] = -alpha * reciprocalA0;
      this.a1[index] = -2 * Math.cos(omega) * reciprocalA0;
      this.a2[index] = (1 - alpha) * reciprocalA0;
      this.gain[index] = 10 ** ((a.gainDb + (b.gainDb - a.gainDb) * morph) / 20);
    }
    this.coefficientMorph = morph;
    this.coefficientsDirty = false;
  }
}

class FormantModuleInstance implements DspModuleInstance {
  private readonly filter: FormantVowelFilterBank;
  constructor(sampleRate: number, private readonly maxBlockSize: number) {
    this.filter = new FormantVowelFilterBank({ sampleRate });
  }
  reset(): void { this.filter.reset(); }
  setParameter(id: string, value: number): void { this.filter.setParameter(id, value); }
  process(block: DspProcessBlock): void {
    if (!Number.isInteger(block.frames) || block.frames < 0 || block.frames > this.maxBlockSize) throw new RangeError('frames exceeds the configured maximum block size');
    const input = block.inputs.in; const output = block.outputs.out; const morph = block.controls.morph;
    if (!input || input.length < block.frames) throw new RangeError('in buffer is missing or too short');
    if (!output || output.length < block.frames) throw new RangeError('out buffer is missing or too short');
    if (morph && morph.length < block.frames) throw new RangeError('morph buffer is too short');
    this.filter.processInto(input, output, morph, block.frames);
  }
}

export const mod020 = defineDspModule({
  descriptor: {
    id: 'mir.mod-020', name: 'Formant-vowel filter bank', version: '0.1.0',
    inputs: [{ id: 'in', kind: 'audio', channels: 1 }, { id: 'morph', kind: 'control', channels: 1 }],
    outputs: [{ id: 'out', kind: 'audio', channels: 1 }],
    parameters: [
      { id: 'bank-a', defaultValue: 0, minValue: 0, maxValue: 7, unit: 'index' },
      { id: 'bank-b', defaultValue: 1, minValue: 0, maxValue: 7, unit: 'index' },
      { id: 'morph', defaultValue: 0, minValue: 0, maxValue: 1, unit: 'ratio' },
      { id: 'bandwidth', defaultValue: 1, minValue: 0.25, maxValue: 4, unit: 'ratio' },
      { id: 'output-gain', defaultValue: 1, minValue: 0, maxValue: 2, unit: 'linear' },
    ],
  },
  createInstance(context) {
    if (!Number.isFinite(context.sampleRate) || context.sampleRate < 8_000 || context.sampleRate > 384_000 || !Number.isInteger(context.maxBlockSize) || context.maxBlockSize < 1) {
      throw new RangeError('invalid DSP instance context');
    }
    return new FormantModuleInstance(context.sampleRate, context.maxBlockSize);
  },
});
